import { useState, useEffect, useCallback } from "react";
import {
  Drawer,
  Typography,
  IconButton,
  Button,
  Box,
  Alert,
  Skeleton,
  Chip,
  Avatar,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Toolbar,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  FolderOpen as FolderOpenIcon,
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon,
  Launch as LaunchIcon,
  DriveFileMoveOutlined as MoveIcon,
  ContentCopy as CloneIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { getBowties, deleteBowtie, moveBowtieToOrg, cloneBowtie } from "../features/bowties/bowtiesSlice";
import MoveOrCopyToOrgDialog from "./MoveOrCopyToOrgDialog";
import { getSeverities } from "../features/severities/severitiesSlice";
import { getThreats } from "../features/threats/threatsSlice";
import { getBarriers } from "../features/barriers/barriersSlice";
import { getConsequences } from "../features/consequences/consequencesSlice";
import { getLikelihoods } from "../features/likelihoods/likelihoodsSlice";

function MyFilesModal({ open, onClose, onCreateBowtie }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { bowties, isLoading, isError, message } = useSelector((state) => state.bowties);
  const { user } = useSelector((state) => state.auth);
  const { activeWorkspace, myOrgs } = useSelector((state) => state.org);

  const isPersonal = activeWorkspace === 'personal';
  const activeOrg = myOrgs.find((o) => o.slug === activeWorkspace);
  const workspaceLabel = isPersonal ? 'Personal' : activeOrg?.name || activeWorkspace;

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [openingBowtie, setOpeningBowtie] = useState(null);
  const [orgDialogBowtie, setOrgDialogBowtie] = useState(null);
  const [busyBowtie, setBusyBowtie] = useState(null);

  // Fetch data when the modal opens or the workspace changes
  useEffect(() => {
    if (open && user?.token) {
      dispatch(getBowties());
      dispatch(getSeverities());
      dispatch(getLikelihoods());
    }
  }, [open, dispatch, user?.token, activeWorkspace]);

  // Handle errors
  useEffect(() => {
    if (isError && message) {
      toast.error(message);
    }
  }, [isError, message]);

  const handleClose = useCallback(() => {
    setDeleteConfirm(null);
    setOpeningBowtie(null);
    onClose();
  }, [onClose]);

  const openBowtie = useCallback(async (bowtieId, bowtieTitle) => {
    if (!user?.token) {
      toast.error("Authentication required");
      return;
    }

    setOpeningBowtie(bowtieId);

    try {
      // Load all required data for the bowtie
      await Promise.all([
        dispatch(getThreats(bowtieId)).unwrap(),
        dispatch(getBarriers(bowtieId)).unwrap(),
        dispatch(getConsequences(bowtieId)).unwrap(),
      ]);

      console.log(`Opening bowtie: ${bowtieTitle}`);
      navigate(`/editor/${bowtieId}`);
      handleClose();

    } catch (error) {
      console.error('Error loading bowtie data:', error);
      toast.error("Failed to load bowtie data. Please try again.");
    } finally {
      setOpeningBowtie(null);
    }
  }, [dispatch, navigate, handleClose, user?.token]);

  const handleDeleteBowtie = useCallback(async (bowtieId) => {
    if (!user?.token) {
      toast.error("Authentication required");
      return;
    }

    try {
      await dispatch(deleteBowtie(bowtieId)).unwrap();
      toast.success("Bowtie deleted successfully");
      setDeleteConfirm(null);
      dispatch(getBowties());
    } catch (error) {
      console.error('Error deleting bowtie:', error);
      toast.error("Failed to delete bowtie. Please try again.");
    }
  }, [dispatch, user?.token]);

  // Add a personal bowtie to an org — move (one-way) or copy (keeps original).
  const handleAddToOrg = useCallback(async ({ action, slug, orgName }) => {
    const bowtie = orgDialogBowtie;
    if (!bowtie) return;
    setBusyBowtie(bowtie._id);
    try {
      if (action === 'move') {
        await dispatch(moveBowtieToOrg({ bowtieId: bowtie._id, organisation: slug })).unwrap();
        toast.success(`Moved to ${orgName}`);
      } else {
        await dispatch(cloneBowtie({ bowtieId: bowtie._id, organisation: slug })).unwrap();
        toast.success(`Copied to ${orgName}`);
      }
      setOrgDialogBowtie(null);
      dispatch(getBowties());
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Could not add this bowtie to the organisation');
    } finally {
      setBusyBowtie(null);
    }
  }, [dispatch, orgDialogBowtie]);

  const handleClone = useCallback(async (bowtieId) => {
    setBusyBowtie(bowtieId);
    try {
      const res = await dispatch(cloneBowtie(bowtieId)).unwrap();
      toast.success('Cloned to your personal workspace');
      return res;
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Could not clone this bowtie');
    } finally {
      setBusyBowtie(null);
    }
  }, [dispatch]);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getBowtieInitials = (title) => {
    return title
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', sm: '450px', md: '500px' },
            backgroundColor: '#fafafa',
            borderLeft: `1px solid ${theme.palette.divider}`,
            boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        {/* Header */}
        <Toolbar 
          sx={{ 
            backgroundColor: '#ffffff',
            borderBottom: `1px solid ${theme.palette.divider}`,
            minHeight: '64px !important',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          {isPersonal ? (
            <PersonIcon sx={{ color: '#2C3E50', mr: 2 }} />
          ) : (
            <GroupsIcon sx={{ color: '#2C3E50', mr: 2 }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, color: '#2C3E50', lineHeight: 1.2 }}
            >
              {isPersonal ? 'My Bowties' : `${workspaceLabel} Bowties`}
            </Typography>
            {!isPersonal && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Shared with everyone in {workspaceLabel}
              </Typography>
            )}
          </Box>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              mr: 2,
              fontWeight: 500
            }}
          >
            {bowties?.length || 0} bowtie{bowties?.length !== 1 ? 's' : ''}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{
              color: '#2C3E50',
              '&:hover': {
                backgroundColor: 'rgba(44, 62, 80, 0.1)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* Loading State */}
          {isLoading && (
            <Box sx={{ p: 3 }}>
              {[...Array(3)].map((_, index) => (
                <Card key={index} sx={{ mb: 2, boxShadow: 1 }}>
                  <CardContent>
                    <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="80%" height={16} />
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Error State */}
          {isError && !isLoading && (
            <Box sx={{ p: 3 }}>
              <Alert 
                severity="error" 
                action={
                  <Button 
                    color="inherit" 
                    size="small"
                    onClick={() => {
                      dispatch(getBowties());
                      dispatch(getSeverities());
                      dispatch(getLikelihoods());
                    }}
                  >
                    Retry
                  </Button>
                }
              >
                <Typography variant="subtitle2" gutterBottom>
                  Failed to Load Bowties
                </Typography>
                <Typography variant="body2">
                  {message || "Unable to load your bowtie files."}
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Empty State */}
          {!isLoading && !isError && (!bowties || bowties.length === 0) && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              minHeight: '60vh',
              p: 4,
              textAlign: 'center'
            }}>
              <FolderOpenIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontFamily: 'Inter, sans-serif' }}>
                No Bowties Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 280 }}>
                Create your first bowtie risk analysis model to get started with professional risk assessment
              </Typography>
              <Button
                variant="contained"
                onClick={onCreateBowtie}
                sx={{
                  backgroundColor: '#E67E22',
                  '&:hover': { backgroundColor: '#D35400' },
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1
                }}
              >
                Create New Bowtie
              </Button>
            </Box>
          )}

          {/* Bowties Grid */}
          {!isLoading && !isError && bowties && bowties.length > 0 && (
            <Box sx={{ p: 3 }}>
              {bowties.map((bowtie) => (
                <Card 
                  key={bowtie._id}
                  sx={{ 
                    mb: 2,
                    boxShadow: 1,
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    opacity: openingBowtie === bowtie._id ? 0.6 : 1,
                    pointerEvents: openingBowtie === bowtie._id ? 'none' : 'auto',
                    '&:hover': {
                      boxShadow: 3,
                      transform: 'translateY(-1px)',
                    }
                  }}
                  onClick={() => openBowtie(bowtie._id, bowtie.title)}
                >
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: '#E67E22',
                          width: 40,
                          height: 40,
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          mr: 2,
                          mt: 0.5
                        }}
                      >
                        {getBowtieInitials(bowtie.title)}
                      </Avatar>
                      
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            mb: 1,
                            lineHeight: 1.3,
                            wordBreak: 'break-word'
                          }}
                        >
                          {bowtie.title}
                          {openingBowtie === bowtie._id && (
                            <Chip 
                              label="Opening..." 
                              size="small" 
                              sx={{ ml: 1, fontSize: '0.7rem' }}
                            />
                          )}
                        </Typography>
                        
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            mb: 0.5,
                            fontSize: '0.8rem'
                          }}
                        >
                          <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          Created: {formatDate(bowtie.createdAt)}
                        </Typography>
                        
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: '0.8rem' }}
                        >
                          Modified: {formatDate(bowtie.updatedAt)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                  
                  <CardActions sx={{ pt: 0, px: 2, pb: 2, justifyContent: 'space-between' }}>
                    <Button
                      size="small"
                      startIcon={<LaunchIcon />}
                      sx={{
                        textTransform: 'none',
                        color: '#2C3E50',
                        fontWeight: 500
                      }}
                    >
                      Open
                    </Button>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      {isPersonal && myOrgs.length > 0 && (
                        <Tooltip title="Move or copy into an organisation">
                          <IconButton
                            size="small"
                            disabled={busyBowtie === bowtie._id}
                            onClick={() => setOrgDialogBowtie(bowtie)}
                            sx={{ color: '#2C3E50' }}
                          >
                            <MoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {!isPersonal && (
                        <Tooltip title="Clone to my personal workspace">
                          <IconButton
                            size="small"
                            disabled={busyBowtie === bowtie._id}
                            onClick={() => handleClone(bowtie._id)}
                            sx={{ color: '#2C3E50' }}
                          >
                            <CloneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <IconButton
                        size="small"
                        onClick={() => setDeleteConfirm(bowtie)}
                        sx={{
                          color: 'error.main',
                          '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardActions>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Move / copy a personal bowtie into an organisation */}
      <MoveOrCopyToOrgDialog
        open={Boolean(orgDialogBowtie)}
        bowtie={orgDialogBowtie}
        orgs={myOrgs}
        busy={busyBowtie === orgDialogBowtie?._id}
        onClose={() => setOrgDialogBowtie(null)}
        onConfirm={handleAddToOrg}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center',
          backgroundColor: '#fff5f5',
          borderBottom: '1px solid #ffebee'
        }}>
          <WarningIcon sx={{ color: 'error.main', mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Delete Bowtie
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to permanently delete this bowtie?
          </Typography>
          <Box sx={{ 
            backgroundColor: '#f5f5f5', 
            p: 2, 
            borderRadius: 1, 
            my: 2,
            border: '1px solid #e0e0e0'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2C3E50' }}>
              "{deleteConfirm?.title}"
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              This action cannot be undone. All associated threats, barriers, and consequences will be permanently deleted.
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button 
            onClick={() => setDeleteConfirm(null)} 
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleDeleteBowtie(deleteConfirm._id)}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{ textTransform: 'none' }}
          >
            Delete Bowtie
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MyFilesModal;
